/**
 * PATH: packages/ui/src/kanban/hooks/useBackendRecorder.ts
 * FILE_ID: UI-KANBAN-RECORDER-001
 * PURPOSE: Capture mic audio with getUserMedia + MediaRecorder and hand the
 *          recorded blob to a host-supplied `transcribe` function (server-side
 *          STT). Browser-independent — works wherever MediaRecorder exists.
 * WHY:
 *   The dictation mic used the Web Speech API (webkitSpeechRecognition), which
 *   only transcribes in official Google Chrome / Safari. In any other Chromium
 *   build `start()` fails with `error:network`. MediaRecorder + getUserMedia are
 *   available in every modern browser, so recording locally and transcribing on
 *   the server is the reliable path (and the foundation for meeting mode).
 *
 * FLOW:
 *   start() → getUserMedia → MediaRecorder.start
 *   stop()  → MediaRecorder.stop → blob → transcribe(blob) → onText(text)
 *
 * DEPENDENCIES:
 *   - KanbanApi.transcribeAudio (injected as `transcribe`)
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { ensureMicPermission } from '../../speech/ensureMicPermission'

export type RecorderPhase = 'idle' | 'recording' | 'transcribing'

export interface UseBackendRecorderArgs {
  /** Host transcription function (KanbanApi.transcribeAudio). */
  transcribe: (blob: Blob, opts?: { language?: string }) => Promise<string>
  language?: string
  /** Called with the final transcript once the upload resolves. */
  onText: (text: string) => void
  /** Called with a stable error code on any failure. */
  onError?: (code: string) => void
}

export interface UseBackendRecorder {
  phase: RecorderPhase
  isSupported: boolean
  start: () => Promise<void>
  stop: () => void
  /** Abort an in-flight recording without transcribing. */
  cancel: () => void
}

function pickMimeType(): string {
  // Prefer formats OpenAI accepts and browsers reliably produce. Safari emits
  // mp4/aac; Chromium emits webm/opus. Fall back to '' (browser default).
  const candidates = ['audio/webm', 'audio/mp4', 'audio/ogg']
  const MR = typeof window !== 'undefined' ? window.MediaRecorder : undefined
  if (MR && typeof MR.isTypeSupported === 'function') {
    for (const t of candidates) {
      try { if (MR.isTypeSupported(t)) return t } catch { /* ignore */ }
    }
  }
  return ''
}

function extForMime(mime: string): string {
  if (mime.includes('mp4')) return 'mp4'
  if (mime.includes('ogg')) return 'ogg'
  return 'webm'
}

export function useBackendRecorder(args: UseBackendRecorderArgs): UseBackendRecorder {
  const { transcribe, language, onText, onError } = args
  const [phase, setPhase] = useState<RecorderPhase>('idle')

  const recorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const cancelledRef = useRef(false)

  const isSupported =
    typeof window !== 'undefined' &&
    typeof window.MediaRecorder !== 'undefined' &&
    !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)

  const teardownStream = useCallback(() => {
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) {
        try { track.stop() } catch { /* ignore */ }
      }
      streamRef.current = null
    }
    recorderRef.current = null
  }, [])

  const start = useCallback(async () => {
    if (!isSupported) {
      onError?.('recorder_unsupported')
      return
    }
    cancelledRef.current = false
    chunksRef.current = []
    const perm = await ensureMicPermission()
    if (perm === 'denied') {
      onError?.('mic_permission_denied')
      return
    }
    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch (e) {
      // NotAllowedError (permission denied) is the dominant case here.
      const name = (e as { name?: string } | null)?.name || 'mic_error'
      onError?.(name === 'NotAllowedError' ? 'mic_permission_denied' : 'mic_error')
      return
    }
    streamRef.current = stream
    const mimeType = pickMimeType()
    let rec: MediaRecorder
    try {
      rec = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
    } catch {
      teardownStream()
      onError?.('recorder_init_failed')
      return
    }
    recorderRef.current = rec

    rec.ondataavailable = (ev: BlobEvent) => {
      if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data)
    }
    rec.onstop = async () => {
      const usedMime = rec.mimeType || mimeType || 'audio/webm'
      teardownStream()
      if (cancelledRef.current) {
        setPhase('idle')
        return
      }
      const blob = new Blob(chunksRef.current, { type: usedMime })
      chunksRef.current = []
      if (blob.size === 0) {
        setPhase('idle')
        onError?.('empty_audio')
        return
      }
      setPhase('transcribing')
      try {
        const text = await transcribe(blob, {
          language,
          // filename extension drives server-side container sniffing
          // (handled by KanbanApi.transcribeAudio default).
          ...(usedMime ? { filename: `dictation.${extForMime(usedMime)}` } : {}),
        } as { language?: string })
        if (text) onText(text)
        setPhase('idle')
      } catch (e) {
        setPhase('idle')
        onError?.(e instanceof Error ? e.message : 'transcription_failed')
      }
    }

    try {
      rec.start()
      setPhase('recording')
    } catch {
      teardownStream()
      setPhase('idle')
      onError?.('recorder_start_failed')
    }
  }, [isSupported, language, onError, onText, teardownStream, transcribe])

  const stop = useCallback(() => {
    const rec = recorderRef.current
    if (rec && rec.state !== 'inactive') {
      try { rec.stop() } catch { /* already stopped */ }
    } else {
      teardownStream()
      setPhase('idle')
    }
  }, [teardownStream])

  const cancel = useCallback(() => {
    cancelledRef.current = true
    stop()
  }, [stop])

  // Stop + release the mic on unmount so a stuck recording can't keep the
  // device's mic indicator on after the drawer closes.
  useEffect(() => () => { cancelledRef.current = true; teardownStream() }, [teardownStream])

  return { phase, isSupported, start, stop, cancel }
}
